import {
  type PointerEvent,
  type ReactNode,
  useId,
  useRef,
  useState,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { Link } from "~/components/link";
import { OverlayBoundary } from "~/components/overlay-boundary";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { useURL } from "~/contexts";
import { useCartSpend } from "~/hec/components/fsa-funds/use-cart-spend";
import useMediaQuery from "~/hec/hooks/useMediaQuery";
import { cn } from "~/lib/ui";
import * as BasketLineItem from "~/routes/resources/components/basket-line-item";
import { useBasketRows } from "~/routes/resources/components/basket-rows-context";
import { MiniCartRowLayout } from "~/routes/resources/components/cart-row-layout";
import { RemovedItemCard } from "~/routes/resources/components/removed-item-card";
import { useFormatPrice } from "~/shop/utils/price";

/** From Tailwind `md` (828px) the ribbon is a sidebar, below it the fixed bar. */
const MD_UP = "(min-width: 828px)";
/** Pointer travel under this is a tap on the handle, over it a drag. */
const TAP_SLOP_PX = 8;
/** px/ms: a flick this fast snaps its way whatever the distance dragged. */
const FLICK_SPEED = 0.4;
const SNAP_MS = 250;

/**
 * Spender cart ribbon (COM-260/263): a sticky sidebar from `md`, a fixed
 * bottom bar below it whose handle drags the details open and shut like a
 * native bottom sheet. `children` is the balance slot.
 */
export function SpenderCartRibbon({
  children,
  skeletonRows = 0,
  className,
}: {
  children?: ReactNode;
  /** Last-known cart line count: the SSR skeleton matches the cart, so nothing shifts. */
  skeletonRows?: number;
  className?: string;
}) {
  const ref = useRef<HTMLElement>(null!);
  const sheetRef = useRef<HTMLDivElement>(null!);
  const detailsRef = useRef<HTMLDivElement>(null!);
  const detailsId = useId();
  const intl = useIntl();
  const [expanded, setExpanded] = useState(false);
  // app.css pads <body> and lifts Zendesk on `body:has(.sticky-atc-bar.fixed)`
  // up to 992px. SSR (false) keeps the class so phones do not shift on hydrate;
  // 828-991px sheds it after hydration.
  const isSidebar = useMediaQuery(MD_UP);
  const url = useURL();

  // The sheet is always laid out at full size and only slides: 0px is open,
  // the details' height is shut. A finger drives an inline `translate`; on
  // release it animates to open or shut and hands `translate` back to CSS.
  const pressY = useRef<number | null>(null);
  const drag = useRef<{
    startY: number;
    startOffset: number;
    lastY: number;
    lastT: number;
    speed: number;
  } | null>(null);
  const offset = () =>
    sheetRef.current.getBoundingClientRect().bottom -
    ref.current.getBoundingClientRect().top;
  const shutOffset = () => detailsRef.current.offsetHeight;
  const settle = (open: boolean) => {
    const sheet = sheetRef.current;
    const from = offset();
    const to = open ? 0 : shutOffset();
    sheet.style.removeProperty("translate");
    setExpanded(open);
    const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
    sheet.animate([{ translate: `0 ${from}px` }, { translate: `0 ${to}px` }], {
      duration: reduced ? 0 : SNAP_MS,
      easing: "cubic-bezier(0.32, 0.72, 0, 1)",
    });
  };
  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (!e.isPrimary) return;
    pressY.current = e.clientY;
    const startOffset = offset();
    sheetRef.current.getAnimations().forEach(a => a.cancel());
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = {
      startY: e.clientY,
      startOffset,
      lastY: e.clientY,
      lastT: e.timeStamp,
      speed: 0,
    };
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    const y = d.startOffset + e.clientY - d.startY;
    sheetRef.current.style.translate = `0 ${Math.min(Math.max(y, 0), shutOffset())}px`;
    const dt = e.timeStamp - d.lastT;
    if (dt > 0) d.speed = (e.clientY - d.lastY) / dt;
    d.lastY = e.clientY;
    d.lastT = e.timeStamp;
  };
  const onPointerEnd = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    // A tap is the click handler's; put back whatever a jitter moved.
    if (
      e.type === "pointercancel" ||
      Math.abs(e.clientY - d.startY) < TAP_SLOP_PX
    ) {
      return settle(expanded);
    }
    settle(
      d.speed < -FLICK_SPEED ||
        (d.speed <= FLICK_SPEED && offset() < shutOffset() / 2),
    );
  };

  const formatPrice = useFormatPrice();
  const { cents, count, known } = useCartSpend();
  const { rows = [], savingForLater } = useBasketRows() ?? {};

  const total = known ? (
    <span data-testid="spender-ribbon-total" className="font-medium">
      {formatPrice(cents / 100)}
    </span>
  ) : (
    <Skeleton
      data-testid="spender-ribbon-total-skeleton"
      className="h-4 w-16"
    />
  );

  return (
    <aside
      ref={ref}
      data-testid="spender-cart-ribbon"
      className={cn(
        "fixed bottom-0 left-0 z-50 w-full bg-white",
        "md:sticky md:bottom-auto md:left-auto md:top-20 md:z-auto md:flex md:max-h-[calc(100dvh_-_6rem)] md:w-[360px] md:shrink-0 md:flex-col md:self-start md:rounded-lg md:border md:border-gray-04",
        !isSidebar && "sticky-atc-bar",
        className,
      )}
    >
      {/* Sits on the bar's top edge; shut, it slides down behind the footer
          until only its border and handle show. */}
      <div
        ref={sheetRef}
        data-testid="spender-ribbon-sheet"
        className={cn(
          "absolute bottom-full left-0 w-full border-t border-minicart-footer-border bg-white",
          // `h-6` is the handle: the shut sheet shows its border and handle.
          !expanded && "translate-y-[calc(100%_-_1px_-_theme(spacing.6))]",
          "md:static md:flex md:min-h-0 md:flex-1 md:translate-y-0 md:flex-col md:border-0",
        )}
      >
        {/* A focusable separator is the ARIA window-splitter pattern, interactive
          per ARIA 1.2; jsx-a11y counts every separator as static. */}
        {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
        <div
          data-testid="spender-ribbon-handle"
          role="separator"
          aria-orientation="horizontal"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={expanded ? 100 : 0}
          aria-controls={detailsId}
          aria-label={intl.formatMessage({
            id: "spenderRibbon.handle",
            defaultMessage: "Resize cart details",
          })}
          tabIndex={0}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
          onPointerCancel={onPointerEnd}
          // Taps, and screen readers' synthetic clicks; a drag's trailing click is ignored.
          onClick={e => {
            const moved = Math.abs(e.clientY - (pressY.current ?? e.clientY));
            pressY.current = null;
            if (moved < TAP_SLOP_PX) settle(!expanded);
          }}
          onKeyDown={e => {
            const next = {
              ArrowUp: true,
              ArrowDown: false,
              Enter: !expanded,
              " ": !expanded,
            }[e.key];
            if (next === undefined) return;
            e.preventDefault();
            settle(next);
          }}
          className="flex h-6 w-full cursor-grab touch-none items-center justify-center md:hidden"
        >
          <span className="h-1.5 w-12 rounded-full bg-minicart-footer-border" />
        </div>
        {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}

        <div
          ref={detailsRef}
          id={detailsId}
          data-testid="spender-ribbon-details"
          inert={!isSidebar && !expanded}
          className="max-h-[calc(100vh_-_13rem)] divide-y divide-gray-04 overflow-auto overscroll-contain border-b border-gray-04 md:max-h-none md:min-h-0 md:flex-1"
        >
          {children && <div className="px-4 pt-4">{children}</div>}
          <div
            data-testid="spender-ribbon-cart-row"
            className="flex items-center justify-between px-4 py-3 font-grotesque font-medium text-blue-03"
          >
            <FormattedMessage
              id="spenderRibbon.your_cart"
              defaultMessage="Your Cart"
            />
            <span
              data-testid="spender-ribbon-count"
              className="text-sm font-light text-gray-03"
            >
              {known ? (
                <FormattedMessage
                  id="spenderRibbon.items"
                  defaultMessage="{count, plural, one {# item} other {# items}}"
                  values={{ count }}
                />
              ) : (
                <Skeleton
                  data-testid="spender-ribbon-count-skeleton"
                  className="inline-block h-4 w-12 align-middle"
                />
              )}
            </span>
          </div>
          {!known && (
            <ul
              data-testid="spender-ribbon-lines-skeleton"
              data-rows={skeletonRows}
              className="flex flex-col gap-2 px-4 py-3"
            >
              {Array.from({ length: skeletonRows }, (_, i) => (
                <MiniCartRowLayout
                  key={i}
                  imgSm={<Skeleton className="h-[72px] w-[72px] rounded-md" />}
                  // Two line boxes in the real name's type: a two-line name's height.
                  name={
                    <div className="font-grotesque text-base font-medium">
                      <Skeleton className="w-full">&nbsp;</Skeleton>
                      <Skeleton className="w-2/3">&nbsp;</Skeleton>
                    </div>
                  }
                  cost={<Skeleton className="h-4 w-12" />}
                  qty={<Skeleton className="h-[48px] w-[112px] rounded-md" />}
                  rm={<Skeleton className="h-[32px] w-[32px] rounded-md" />}
                />
              ))}
            </ul>
          )}
          {known && rows.length > 0 && (
            <ul className="flex flex-col gap-2 px-4 py-3">
              {rows.map((row, rowIndex) =>
                row.kind === "normal" ? (
                  <OverlayBoundary
                    key={row.lineItem.itemId}
                    overlay="spender-ribbon-row"
                    fallback={
                      <li
                        data-testid="spender-ribbon-row-error"
                        className="py-2 text-xs"
                      >
                        <FormattedMessage
                          id="spenderRibbon.row_error"
                          defaultMessage="This item could not be shown. It is still in your cart."
                        />
                      </li>
                    }
                  >
                    <BasketLineItem.Root
                      lineItem={row.lineItem}
                      boundryRef={ref}
                      scope="ribbon"
                      rowIndex={rowIndex}
                    >
                      {isStale => (
                        <MiniCartRowLayout
                          isStale={isStale}
                          isSavingForLater={savingForLater?.has(
                            row.lineItem.itemId ?? "",
                          )}
                          imgSm={<BasketLineItem.ProductImage size="xs" />}
                          name={
                            <BasketLineItem.ProductName className="text-wrap text-sm" />
                          }
                          cost={<BasketLineItem.ProductPriceUnit />}
                          qty={<BasketLineItem.ProductQuantity />}
                          rm={<BasketLineItem.RemoveProduct />}
                        />
                      )}
                    </BasketLineItem.Root>
                  </OverlayBoundary>
                ) : (
                  <RemovedItemCard
                    key={`removed-${row.itemId}`}
                    className="p-0"
                    snap={row.snap}
                  />
                ),
              )}
            </ul>
          )}
          {known && rows.length === 0 && (
            <p
              data-testid="spender-ribbon-empty-message"
              className="px-4 py-6 text-center text-sm font-light text-gray-05"
            >
              <FormattedMessage
                id="spenderRibbon.empty"
                defaultMessage="Your cart is empty"
              />
            </p>
          )}
        </div>
      </div>

      {/* Positioned, so it paints over the shut sheet behind it. */}
      <div className="relative flex flex-col gap-3 bg-white p-4">
        <div
          data-testid="spender-ribbon-estimated-total-row"
          className="flex items-center justify-between font-medium text-blue-03"
        >
          <FormattedMessage
            id="spenderRibbon.estimated_total"
            defaultMessage="Estimated Total"
          />
          {total}
        </div>
        <Button
          as={Link}
          to={url("/cart")}
          prefetch="intent"
          variant="cta"
          size="4"
          className="w-full"
          data-testid="spender-ribbon-view-cart"
        >
          <FormattedMessage
            id="spenderRibbon.view_cart"
            defaultMessage="View Cart"
          />
        </Button>
      </div>
    </aside>
  );
}
